"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Layers, Globe, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function ClosingCTA() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut",
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  const journeySteps = [{
    vi: "Đội ngũ",
    en: "Team"
  }, {
    vi: "Học tập",
    en: "Learning"
  }, {
    vi: "Dự án",
    en: "Projects"
  }, {
    vi: "Trải nghiệm học viên",
    en: "Student Experience"
  }];
  return <section id="closingcta" data-nav="dark" className="relative bg-slate-950 text-slate-100 overflow-hidden py-20 lg:py-28">
      {/* Engineered Lab Blueprint Grid Background */}
      <div className="aria-hidden:true pointer-events-none absolute inset-0 opacity-20 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:32px_32px]" aria-hidden="true" />

      {/* Subtle radial glow overlay */}
      <div className="aria-hidden:true pointer-events-none absolute -top-40 -left-40 size-[32rem] rounded-full bg-blue-600/20 blur-3xl" aria-hidden="true" />
      <div className="aria-hidden:true pointer-events-none absolute -bottom-40 -right-40 size-[32rem] rounded-full bg-secondary/20 blur-3xl" aria-hidden="true" />

      {/* Blueprint Corner Crosshairs */}
      <div className="aria-hidden:true pointer-events-none absolute top-6 left-6 text-slate-700 font-mono text-xs" aria-hidden="true">
        + [SYS.ROBOTO.CTA // 04]
      </div>
      <div className="aria-hidden:true pointer-events-none absolute top-6 right-6 text-slate-700 font-mono text-xs" aria-hidden="true">
        [BLUEPRINT_GRID_2025] +
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language Switcher Bar */}
        <div className="flex justify-end mb-6">
          <div className="inline-flex items-center gap-1.5 bg-slate-900/90 border border-slate-800 rounded-full p-1 text-xs font-medium">
            <Globe className="size-3.5 text-slate-400 ml-1.5" />
            <Button type="button" variant="ghost" onClick={() => setLang("vi")} className={`px-2.5 py-1 h-auto rounded-full transition-colors ${lang === "vi" ? "bg-primary text-primary-foreground font-semibold hover:bg-primary hover:text-primary-foreground" : "text-slate-400 hover:text-slate-200 hover:bg-transparent"}`}>
              TIẾNG VIỆT
            </Button>
            <Button type="button" variant="ghost" onClick={() => setLang("en")} className={`px-2.5 py-1 h-auto rounded-full transition-colors ${lang === "en" ? "bg-primary text-primary-foreground font-semibold hover:bg-primary hover:text-primary-foreground" : "text-slate-400 hover:text-slate-200 hover:bg-transparent"}`}>
              ENGLISH
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="space-y-12">
          {/* Header Block */}
          <motion.div variants={itemVariants} className="text-center max-w-3xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-300 text-xs font-mono">
              <Sparkles className="size-3.5 text-accent" />
              <span>
                {lang === "vi" ? "HỆ SINH THÁI GIÁO DỤC CÔNG NGHỆ ROBOTO" : "ROBOTO TECHNOLOGY EDUCATION ECOSYSTEM"}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-serif">
              {lang === "vi" ? "Sẵn sàng định hình tương lai công nghệ?" : "Ready to shape the technological future?"}
            </h2>
            <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              {lang === "vi" ? "Khám phá môi trường học tập chuẩn quốc tế, trang bị tư duy sáng tạo và kỹ năng kỹ thuật vững chắc cho thế hệ trẻ." : "Discover an international-standard learning environment equipping young minds with creative thinking and solid engineering skills."}
            </p>
          </motion.div>

          {/* Relationship Framework Band: Team -> Learning -> Projects -> Student Experience */}
          <motion.div variants={itemVariants} className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl backdrop-blur-sm">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-xs font-mono text-slate-400 uppercase tracking-wider">
                {lang === "vi" ? "Hành trình kết nối:" : "Cohesive Journey:"}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-sm font-medium text-slate-200">
                {journeySteps.map((step, idx) => <div key={idx} className="flex items-center gap-2 sm:gap-4" data-index={idx}>
                    <span className="px-3 py-1 rounded-lg bg-slate-800/80 border border-slate-700/60 text-slate-200">
                      {lang === "vi" ? step.vi : step.en}
                    </span>
                    {idx < journeySteps.length - 1 && <ChevronRight className="size-4 text-slate-500 shrink-0" />}
                  </div>)}
              </div>
              <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary/90 hover:bg-slate-800/50 font-semibold text-xs tracking-wider uppercase transition-colors">
                <Link to="/ProjectsCompetitionsPage">
                  <span>
                    {lang === "vi" ? "KHÁM PHÁ CÁC DỰ ÁN HỌC VIÊN" : "Explore Student Projects"}
                  </span>
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Three Main Action Cards Section wrapped with Supporting Deep-Blue/White Technical Banner Photography Background */}
          <motion.div variants={itemVariants} className="relative rounded-3xl overflow-hidden border border-blue-900/40 bg-slate-900/80 p-6 sm:p-8 lg:p-10 shadow-2xl">
            {/* Supporting Technical Banner Photograph */}
            <div className="aria-hidden:true pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_tech_lab_banner.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_tech_lab_banner.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_tech_lab_banner.png-wvc-sizes" alt="ROBOTO Technical Academy Student Robotics Engineering Lab" loading="lazy" className="w-full h-full object-cover object-center opacity-30 mix-blend-luminosity filter contrast-125 scale-105" />
              {/* Deep Blue & Slate Technical Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-blue-950/80 to-slate-950/95" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-slate-950/80" />
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-blue-500/10 to-transparent" />
            </div>

            {/* Technical Spec Frame Accents */}
            <div className="aria-hidden:true pointer-events-none absolute top-3 left-4 flex items-center gap-2 font-mono text-[10px] text-blue-300/60 uppercase tracking-widest z-10" aria-hidden="true">
              <span className="size-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span>LAB_SPEC // ROBOTO_TECH_SPREAD</span>
            </div>

            <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
              {/* Primary CTA */}
              <motion.div variants={itemVariants} whileHover={{
              y: -2
            }} transition={{
              duration: 0.15
            }}>
                <Card className="h-full bg-slate-900/90 border-slate-800 hover:border-primary/50 text-slate-100 flex flex-col justify-between shadow-xl transition-all backdrop-blur-md">
                  <CardContent className="p-6 flex flex-col justify-between h-full gap-6">
                    <div className="space-y-3">
                      <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                        <Sparkles className="size-5" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        {lang === "vi" ? "Tư vấn & Trải nghiệm" : "Consultation & Trial"}
                      </h3>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {lang === "vi" ? "Đăng ký tư vấn trực tiếp và tham gia lớp học thử nghiệm robotics chuẩn quốc tế." : "Register for personalized guidance and join an international-standard robotics trial session."}
                      </p>
                    </div>
                    <Button asChild variant="default" size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md border border-primary/30">
                      <Link to="/FreeTrialContactPage">
                        <span>
                          {lang === "vi" ? "ĐĂNG KÝ TƯ VẤN" : "Book a Consultation"}
                        </span>
                        <ArrowRight className="size-4 ml-1.5" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Secondary CTA */}
              <motion.div variants={itemVariants} whileHover={{
              y: -2
            }} transition={{
              duration: 0.15
            }}>
                <Card className="h-full bg-slate-900/90 border-slate-800 hover:border-secondary/50 text-slate-100 flex flex-col justify-between shadow-xl transition-all backdrop-blur-md">
                  <CardContent className="p-6 flex flex-col justify-between h-full gap-6">
                    <div className="space-y-3">
                      <div className="size-10 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary">
                        <Compass className="size-5" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        {lang === "vi" ? "Lộ trình Robotics" : "Robotics Pathway"}
                      </h3>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {lang === "vi" ? "Khám phá lộ trình phát triển năng lực từng bước từ độ tuổi 4 đến 16." : "Explore step-by-step skill development tailored for ages 4 through 16."}
                      </p>
                    </div>
                    <Button asChild variant="secondary" size="lg" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold shadow-md border border-secondary/30">
                      <Link to="/RoboticsLearningPathwayPage">
                        <span>
                          {lang === "vi" ? "XEM LỘ TRÌNH ROBOTICS" : "View the Robotics Pathway"}
                        </span>
                        <ArrowRight className="size-4 ml-1.5" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Tertiary CTA */}
              <motion.div variants={itemVariants} whileHover={{
              y: -2
            }} transition={{
              duration: 0.15
            }}>
                <Card className="h-full bg-slate-900/90 border-slate-800 hover:border-slate-700 text-slate-100 flex flex-col justify-between shadow-xl transition-all backdrop-blur-md">
                  <CardContent className="p-6 flex flex-col justify-between h-full gap-6">
                    <div className="space-y-3">
                      <div className="size-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-200">
                        <Layers className="size-5" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        {lang === "vi" ? "Hệ sinh thái đào tạo" : "Program Ecosystem"}
                      </h3>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        {lang === "vi" ? "Tìm hiểu hệ sinh thái chương trình của ROBOTO, từ Robotics đến Coding, Engineering, STEM và các chương trình chuyên sâu." : "Explore ROBOTO's ecosystem of programs, from Robotics to Coding, Engineering, STEM and specialized tracks."}
                      </p>
                    </div>
                    <Button asChild variant="outline" size="lg" className="w-full border-slate-700 bg-slate-800/80 text-slate-100 hover:bg-slate-800 hover:text-white font-semibold transition-colors">
                      <Link to="/ProgramsPage">
                        <span>
                          {lang === "vi" ? "XEM CHƯƠNG TRÌNH" : "View Programs"}
                        </span>
                        <ArrowRight className="size-4 ml-1.5" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}